'use client';

import type { IconButtonProps } from '@mui/material/IconButton';

import { m } from 'framer-motion';
import { useState, useCallback, useMemo } from 'react';

import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Badge from '@mui/material/Badge';
import Drawer from '@mui/material/Drawer';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';

import { NotificationItem } from './notification-item';
import { useNotifications } from './useNotifications';

import { useBoolean } from '@/components/common/hooks/use-boolean';
import { Iconify } from '@/components/common/iconify';
import { Label } from '@/components/common/label';
import { Scrollbar } from '@/components/common/scrollbar';
import { varHover } from '@/components/common/animate';
import Tabs from '@mui/material/Tabs';

// ----------------------------------------------------------------------

export type NotificationsDrawerProps = IconButtonProps;

export function NotificationsDrawer({ sx, ...other }: NotificationsDrawerProps) {
  const drawer = useBoolean();

  const [currentTab, setCurrentTab] = useState('all');

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const { notifications, loading, degraded, markAsRead, markAllAsRead } = useNotifications();

  const totalUnRead = notifications.filter((item) => item.isUnRead === true).length;
  const totalAll = notifications.length;
  const totalUnread = notifications.filter((item) => item.isUnRead).length;

  const filteredNotifications = useMemo(() => {
    if (currentTab === 'unread') return notifications.filter((n) => n.isUnRead);
    if (currentTab === 'archived') return notifications.filter((n) => !n.isUnRead);
    return notifications;
  }, [notifications, currentTab]);

  const tabs = [
    { value: 'all',      label: 'All',      count: totalAll },
    { value: 'unread',   label: 'Unread',   count: totalUnread },
    { value: 'archived', label: 'Archived', count: totalAll - totalUnread },
  ];

  const renderHead = (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        pt: 2.5,
        pb: 2,
        pl: 2.5,
        pr: 1,
        minHeight: 68,
        flexShrink: 0,
      }}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        Notifications
      </Typography>

      {!!totalUnRead && (
        <Tooltip title="Mark all as read">
          <IconButton color="primary" onClick={markAllAsRead}>
            <Iconify icon="eva:done-all-fill" />
          </IconButton>
        </Tooltip>
      )}

      <Tooltip title="Close">
        <IconButton onClick={drawer.onFalse} aria-label="Close notifications">
          <Iconify icon="mingcute:close-line" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const renderTabs = (
    <Tabs
      value={currentTab}
      onChange={handleChangeTab}
      sx={{
        px: 2.5,
        borderBottom: 1,
        borderColor: 'divider',
        minHeight: 44,
        '& .MuiTabs-indicator': { height: 2, bgcolor: 'primary.main' },
        '& .Mui-selected': { color: 'primary.main', fontWeight: 700 },
      }}
    >
      {tabs.map((tab) => (
        <Tab
          key={tab.value}
          iconPosition="end"
          value={tab.value}
          label={tab.label}
          sx={{ minHeight: 44, px: 1.5, mr: 1 }}
          icon={
            <Label
              variant={tab.value === currentTab ? 'filled' : 'soft'}
              color={
                (tab.value === 'unread' && 'info') ||
                (tab.value === 'archived' && 'default') ||
                'primary'
              }
              sx={{ ml: 0.5 }}
            >
              {tab.count}
            </Label>
          }
        />
      ))}
    </Tabs>
  );

  const renderList = (
    <Scrollbar>
      {loading && !notifications.length ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : !filteredNotifications.length ? (
        <Stack alignItems="center" justifyContent="center" sx={{ py: 5 }}>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            No notifications
          </Typography>
        </Stack>
      ) : (
        <Box component="ul">
          {filteredNotifications.map((notification) => (
            <Box component="li" key={notification.id} sx={{ display: 'flex' }}>
              <NotificationItem
                notification={notification}
                onClick={notification.isUnRead ? () => markAsRead(notification.id) : undefined}
              />
            </Box>
          ))}
        </Box>
      )}
    </Scrollbar>
  );

  return (
    <>
      <Tooltip title={degraded ? 'Notifications unavailable' : 'Notifications'}>
        <IconButton
          component={m.button}
          whileTap="tap"
          whileHover="hover"
          variants={varHover(1.05)}
          onClick={drawer.onTrue}
          sx={{ ...(degraded && { opacity: 0.5 }), ...sx }}
          {...other}
        >
          <Badge badgeContent={totalUnRead} color="error">
            <Iconify icon="solar:bell-bold" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer
        open={drawer.value}
        onClose={drawer.onFalse}
        anchor="right"
        slotProps={{ backdrop: { invisible: true } }}
        PaperProps={{
          sx: {
            width: 1,
            maxWidth: 420,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            bgcolor: 'background.paper',
            backgroundImage: 'none',
          },
        }}
        sx={{ zIndex: 1350 }}
      >
        <Box sx={{ flexShrink: 0, pt: 0.5 }}>
          {renderHead}
        </Box>

        {renderTabs}

        {renderList}
      </Drawer>
    </>
  );
}
